/**
 * TronGrid — Swedish Tron-style animated 3D grid background.
 *
 * Classic 80s Tron floor grid seen from an angle with:
 *  - Custom line-based grid (not drei Grid) for full control
 *  - Light pulses traveling along grid lines in both directions
 *  - Bloom post-processing for neon glow
 *  - Camera angled down at the grid like the lightcycle arena
 *
 * Colors: Swedish blue #006AA7, Swedish yellow #FECC00
 */

import { useRef, useMemo } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import {
  EffectComposer,
  Bloom,
  Vignette,
} from "@react-three/postprocessing";
import * as THREE from "three";

const BLUE = new THREE.Color("#006AA7");
const YELLOW = new THREE.Color("#FECC00");
const GRID_SIZE = 160;
const GRID_DIVISIONS = 80;
const SPACING = GRID_SIZE / GRID_DIVISIONS;


/* ── The floor grid — custom LineSegments for crisp Tron lines ── */
function FloorGrid() {
  const ref = useRef<THREE.LineSegments>(null!);

  const geometry = useMemo(() => {
    const pts: number[] = [];
    const half = GRID_SIZE / 2;
    // Lines along Z (running away from camera)
    for (let i = 0; i <= GRID_DIVISIONS; i++) {
      const x = -half + i * SPACING;
      pts.push(x, 0, -half, x, 0, half);
    }
    // Lines along X (left to right)
    for (let i = 0; i <= GRID_DIVISIONS; i++) {
      const z = -half + i * SPACING;
      pts.push(-half, 0, z, half, 0, z);
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.Float32BufferAttribute(pts, 3));
    return geo;
  }, []);

  return (
    <lineSegments ref={ref} geometry={geometry} position={[0, 0, 0]}>
      <lineBasicMaterial color={BLUE} transparent opacity={0.1} />
    </lineSegments>
  );
}

/* ── Brighter section lines every 4th division ── */
function SectionGrid() {
  const geometry = useMemo(() => {
    const pts: number[] = [];
    const half = GRID_SIZE / 2;
    const sectionSpacing = SPACING * 4;
    const sectionCount = GRID_DIVISIONS / 4;
    for (let i = 0; i <= sectionCount; i++) {
      const x = -half + i * sectionSpacing;
      pts.push(x, 0.001, -half, x, 0.001, half);
    }
    for (let i = 0; i <= sectionCount; i++) {
      const z = -half + i * sectionSpacing;
      pts.push(-half, 0.001, z, half, 0.001, z);
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.Float32BufferAttribute(pts, 3));
    return geo;
  }, []);

  return (
    <lineSegments geometry={geometry} position={[0, 0, 0]}>
      <lineBasicMaterial color={BLUE} transparent opacity={0.25} />
    </lineSegments>
  );
}

/* ── Vertical light pulse traveling along a Z grid line (away from camera) ── */
function GridLightPulse({
  laneX,
  speed,
  color,
  length,
}: {
  laneX: number;
  speed: number;
  color: THREE.Color;
  length: number;
}) {
  const ref = useRef<THREE.Mesh>(null!);
  const half = GRID_SIZE / 2;

  useFrame(({ clock }) => {
    const t = clock.elapsedTime;
    // Pulse travels along the Z axis, clamped within the grid bounds
    const raw = ((t * speed + laneX * 17.3) % GRID_SIZE) - half;
    ref.current.position.set(laneX, 0.01, raw);
  });

  return (
    <mesh ref={ref}>
      <boxGeometry args={[0.06, 0.02, length]} />
      <meshBasicMaterial color={color} transparent opacity={0.85} />
    </mesh>
  );
}

/* ── Vertical light pulses running along Z grid lines ── */
function LightPulses() {
  const lanes = useMemo(() => {
    const arr: { laneX: number; speed: number; color: THREE.Color; length: number }[] = [];
    const sectionSpacing = SPACING * 4; // pulses snap to section grid lines
    const half = GRID_SIZE / 2;
    // Place pulses on section-grid X positions (every 4th line)
    for (let i = -4; i <= 4; i++) {
      const x = i * sectionSpacing;
      if (Math.abs(x) <= half) {
        const isYellow = i % 2 === 0;
        arr.push({
          laneX: x,
          speed: 7 + Math.random() * 7,
          color: isYellow ? YELLOW : BLUE,
          length: 3 + Math.random() * 5,
        });
      }
    }
    return arr;
  }, []);

  return (
    <group>
      {lanes.map((l, i) => (
        <GridLightPulse key={i} {...l} />
      ))}
    </group>
  );
}

/* ── Horizon glow — a soft light at the vanishing point ── */
function HorizonGlow() {
  return (
    <mesh position={[0, -0.5, -GRID_SIZE / 2]} rotation={[-Math.PI / 2, 0, 0]}>
      <planeGeometry args={[GRID_SIZE, 8]} />
      <meshBasicMaterial
        color={BLUE}
        transparent
        opacity={0.08}
        side={THREE.DoubleSide}
      />
    </mesh>
  );
}

/* ── Star field — twinkling points in the sky above the grid ── */
function StarField({ count = 600 }: { count?: number }) {
  const ref = useRef<THREE.Points>(null!);

  const { positions, sizes, phases } = useMemo(() => {
    const pos = new Float32Array(count * 3);
    const sz = new Float32Array(count);
    const ph = new Float32Array(count);
    for (let i = 0; i < count; i++) {
      // Spread stars across a wide dome above the grid
      pos[i * 3] = (Math.random() - 0.5) * 300;       // X: wide spread
      pos[i * 3 + 1] = 5 + Math.random() * 120;       // Y: above the grid
      pos[i * 3 + 2] = -150 + Math.random() * 200;     // Z: behind and around
      sz[i] = 0.3 + Math.random() * 1.2;
      ph[i] = Math.random() * Math.PI * 2;
    }
    return { positions: pos, sizes: sz, phases: ph };
  }, [count]);

  useFrame(({ clock }) => {
    const t = clock.elapsedTime;
    const sizeAttr = ref.current.geometry.getAttribute("size") as THREE.BufferAttribute;
    const arr = sizeAttr.array as Float32Array;
    for (let i = 0; i < count; i++) {
      // Gentle twinkle: oscillate size between 40% and 100% of base size
      arr[i] = sizes[i] * (0.4 + 0.6 * (0.5 + 0.5 * Math.sin(t * (0.5 + phases[i] * 0.3) + phases[i])));
    }
    sizeAttr.needsUpdate = true;
  });

  return (
    <points ref={ref}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
        <bufferAttribute attach="attributes-size" args={[sizes, 1]} />
      </bufferGeometry>
      <pointsMaterial
        color="#ffffff"
        transparent
        opacity={0.7}
        sizeAttenuation
        size={1}
        depthWrite={false}
      />
    </points>
  );
}

/* ── Main exported component ── */
export function TronGrid() {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 0,
        background: "#000a12",
      }}
    >
      <Canvas
        camera={{
          position: [0, 1.8, 30],
          rotation: [-0.04, 0, 0],
          fov: 68,
          near: 0.1,
          far: 300,
        }}
        dpr={[1, 1.5]}
        gl={{ antialias: true, alpha: false }}
        style={{ background: "#000a12" }}
      >
        <fog attach="fog" args={["#000a12", 60, 150]} />

        {/* Sky — twinkling stars above the grid horizon */}
        <StarField />

        {/* The grid floor — horizon meets the title, near edge past camera to fill viewport bottom */}
        <group position={[0, -1.2, -40]}>
          <FloorGrid />
          <SectionGrid />
          <LightPulses />
          <HorizonGlow />
        </group>

        {/* Post-processing: bloom makes the lines glow */}
        <EffectComposer>
          <Bloom
            intensity={1.5}
            luminanceThreshold={0.08}
            luminanceSmoothing={0.3}
            mipmapBlur
          />
          <Vignette offset={0.3} darkness={0.75} />
        </EffectComposer>
      </Canvas>
    </div>
  );
}